import { useCallback, useEffect, useRef, useState } from 'react';

const VAD_SAMPLE_RATE = 16000;
const MIN_SPEECH_MS = 900;
const MIN_AUDIO_BYTES = 2400;
const IDLE_TIMEOUT_MS = 40000;
const SPEAKER_TAIL_SUPPRESS_MS = 1200;
const BARGE_IN_GRACE_MS = 900;
const BARGE_IN_CONFIRM_MS = 1300;
const BARGE_IN_MIN_SPEECH_MS = 1300;

const NORMAL_VAD_OPTIONS = {
    positiveSpeechThreshold: 0.52,
    negativeSpeechThreshold: 0.25,
    minSpeechMs: MIN_SPEECH_MS,
    redemptionMs: 1600,
    preSpeechPadMs: 320,
};

const BARGE_IN_VAD_OPTIONS = {
    positiveSpeechThreshold: 0.86,
    negativeSpeechThreshold: 0.62,
    minSpeechMs: BARGE_IN_MIN_SPEECH_MS,
    redemptionMs: 800,
    preSpeechPadMs: 160,
};

const handsFreeLabels = {
    off: 'Hands-free',
    starting: 'Starting...',
    listening: 'Listening',
    recording: 'Recording',
    thinking: 'Thinking',
    speaking: 'Speaking',
    error: 'Voice unavailable',
};

function arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
}

export function useHandsFreeVoice({
    onAudioReady,
    onBargeIn,
    disabled = false,
    assistantSpeaking = false,
}) {
    const [enabled, setEnabled] = useState(false);
    const [mode, setMode] = useState('off');
    const [error, setError] = useState('');
    const vadRef = useRef(null);
    const enabledRef = useRef(false);
    const busyRef = useRef(false);
    const sendingRef = useRef(false);
    const stoppedRef = useRef(false);
    const assistantSpeakingRef = useRef(false);
    const assistantSpeakingStartedAtRef = useRef(0);
    const bargeInStartedRef = useRef(false);
    const bargeInConfirmTimerRef = useRef(null);
    const idleTimerRef = useRef(null);
    const resumeTimerRef = useRef(null);
    const onAudioReadyRef = useRef(onAudioReady);
    const onBargeInRef = useRef(onBargeIn);

    useEffect(() => {
        onAudioReadyRef.current = onAudioReady;
    }, [onAudioReady]);

    useEffect(() => {
        onBargeInRef.current = onBargeIn;
    }, [onBargeIn]);

    const pauseVad = useCallback(async () => {
        try {
            if (vadRef.current?.listening) {
                await vadRef.current.pause();
            }
        } catch (err) {
            console.warn('[HandsFree] Failed to pause VAD', err);
        }
    }, []);

    const clearIdleTimer = useCallback(() => {
        if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current);
            idleTimerRef.current = null;
        }
    }, []);

    const clearResumeTimer = useCallback(() => {
        if (resumeTimerRef.current) {
            clearTimeout(resumeTimerRef.current);
            resumeTimerRef.current = null;
        }
    }, []);

    const clearBargeInTimer = useCallback(() => {
        if (bargeInConfirmTimerRef.current) {
            clearTimeout(bargeInConfirmTimerRef.current);
            bargeInConfirmTimerRef.current = null;
        }
    }, []);

    const armIdleTimer = useCallback(() => {
        clearIdleTimer();
        if (!enabledRef.current || stoppedRef.current || busyRef.current || assistantSpeakingRef.current) return;

        idleTimerRef.current = setTimeout(() => {
            if (!enabledRef.current || stoppedRef.current || busyRef.current || assistantSpeakingRef.current) return;
            enabledRef.current = false;
            stoppedRef.current = true;
            sendingRef.current = false;
            setEnabled(false);
            setMode('off');
            setError('');
            if (vadRef.current) {
                vadRef.current.destroy().catch((err) => {
                    console.warn('[HandsFree] Failed to destroy VAD after idle timeout', err);
                });
                vadRef.current = null;
            }
        }, IDLE_TIMEOUT_MS);
    }, [clearIdleTimer]);

    const startVad = useCallback(async () => {
        if (
            !vadRef.current ||
            !enabledRef.current ||
            busyRef.current ||
            stoppedRef.current
        ) return;
        try {
            await vadRef.current.start();
            if (enabledRef.current && !busyRef.current) {
                setMode(assistantSpeakingRef.current ? 'speaking' : 'listening');
            }
        } catch (err) {
            console.error('[HandsFree] Failed to start VAD', err);
            setError('Could not start microphone listening.');
            setMode('error');
        }
    }, []);

    const stop = useCallback(async () => {
        enabledRef.current = false;
        stoppedRef.current = true;
        setEnabled(false);
        setMode('off');
        setError('');
        sendingRef.current = false;
        bargeInStartedRef.current = false;
        clearBargeInTimer();
        clearResumeTimer();
        clearIdleTimer();

        try {
            if (vadRef.current) {
                await vadRef.current.destroy();
            }
        } catch (err) {
            console.warn('[HandsFree] Failed to destroy VAD', err);
        } finally {
            vadRef.current = null;
        }
    }, [clearBargeInTimer, clearIdleTimer, clearResumeTimer]);

    const destroySilently = useCallback(() => {
        enabledRef.current = false;
        stoppedRef.current = true;
        sendingRef.current = false;
        bargeInStartedRef.current = false;
        clearBargeInTimer();
        clearResumeTimer();
        clearIdleTimer();
        if (vadRef.current) {
            vadRef.current.destroy().catch((err) => {
                console.warn('[HandsFree] Failed to destroy VAD during cleanup', err);
            });
            vadRef.current = null;
        }
    }, [clearBargeInTimer, clearIdleTimer, clearResumeTimer]);

    const start = useCallback(async () => {
        if (disabled || enabledRef.current) return;

        enabledRef.current = true;
        stoppedRef.current = false;
        setEnabled(true);
        setMode('starting');
        setError('');

        try {
            const { MicVAD, utils } = await import('@ricky0123/vad-web');
            vadRef.current = await MicVAD.new({
                model: 'v5',
                baseAssetPath: '/vad/',
                onnxWASMBasePath: '/vad/',
                ...NORMAL_VAD_OPTIONS,
                startOnLoad: false,
                onSpeechStart: () => {
                    if (!enabledRef.current || busyRef.current) return;
                    setMode(assistantSpeakingRef.current ? 'speaking' : 'recording');
                    clearIdleTimer();
                },
                onSpeechRealStart: () => {
                    if (!enabledRef.current || busyRef.current) return;
                    clearIdleTimer();
                    if (assistantSpeakingRef.current) {
                        const elapsed = Date.now() - assistantSpeakingStartedAtRef.current;
                        if (elapsed < BARGE_IN_GRACE_MS || bargeInStartedRef.current) {
                            return;
                        }

                        clearBargeInTimer();
                        bargeInConfirmTimerRef.current = setTimeout(() => {
                            if (
                                !enabledRef.current ||
                                stoppedRef.current ||
                                busyRef.current ||
                                !assistantSpeakingRef.current ||
                                bargeInStartedRef.current
                            ) return;

                            bargeInStartedRef.current = true;
                            onBargeInRef.current?.();
                            setMode('recording');
                        }, BARGE_IN_CONFIRM_MS);
                        return;
                    }
                    setMode('recording');
                },
                onVADMisfire: () => {
                    if (!enabledRef.current || busyRef.current) return;
                    clearBargeInTimer();
                    bargeInStartedRef.current = false;
                    setMode(assistantSpeakingRef.current ? 'speaking' : 'listening');
                    armIdleTimer();
                },
                onSpeechEnd: async (audio) => {
                    if (!enabledRef.current || busyRef.current || sendingRef.current) return;
                    clearBargeInTimer();

                    if (assistantSpeakingRef.current && !bargeInStartedRef.current) {
                        setMode('speaking');
                        return;
                    }

                    const durationMs = (audio.length / VAD_SAMPLE_RATE) * 1000;
                    const minSpeechMs = bargeInStartedRef.current ? BARGE_IN_MIN_SPEECH_MS : MIN_SPEECH_MS;
                    if (durationMs < minSpeechMs) {
                        bargeInStartedRef.current = false;
                        setMode(assistantSpeakingRef.current ? 'speaking' : 'listening');
                        armIdleTimer();
                        return;
                    }

                    try {
                        sendingRef.current = true;
                        busyRef.current = true;
                        setMode('thinking');
                        await pauseVad();

                        const wavBuffer = utils.encodeWAV(audio, 1, VAD_SAMPLE_RATE, 1, 16);
                        if (wavBuffer.byteLength < MIN_AUDIO_BYTES) {
                            sendingRef.current = false;
                            busyRef.current = false;
                            bargeInStartedRef.current = false;
                            setMode(assistantSpeakingRef.current ? 'speaking' : 'listening');
                            await startVad();
                            armIdleTimer();
                            return;
                        }

                        await onAudioReadyRef.current?.({
                            filename: `hands_free_${Date.now()}.wav`,
                            mime_type: 'audio/wav',
                            base64: arrayBufferToBase64(wavBuffer),
                        });
                    } catch (err) {
                        console.error('[HandsFree] Failed to send voice turn', err);
                        setError('Could not send voice message.');
                        setMode('error');
                        busyRef.current = false;
                    } finally {
                        sendingRef.current = false;
                        bargeInStartedRef.current = false;
                        armIdleTimer();
                    }
                },
            });

            if (!enabledRef.current || stoppedRef.current) {
                await stop();
                return;
            }

            await startVad();
            armIdleTimer();
        } catch (err) {
            console.error('[HandsFree] Failed to initialize VAD', err);
            setError('Microphone or voice detection is unavailable.');
            setMode('error');
            enabledRef.current = false;
            setEnabled(false);
            clearIdleTimer();
        }
    }, [armIdleTimer, clearBargeInTimer, clearIdleTimer, disabled, pauseVad, startVad, stop]);

    const toggle = useCallback(() => {
        if (enabledRef.current) {
            stop();
        } else {
            start();
        }
    }, [start, stop]);

    useEffect(() => {
        clearResumeTimer();

        if (assistantSpeaking && !assistantSpeakingRef.current) {
            assistantSpeakingStartedAtRef.current = Date.now();
            bargeInStartedRef.current = false;
        }
        assistantSpeakingRef.current = Boolean(assistantSpeaking);
        busyRef.current = Boolean(disabled);

        vadRef.current?.setOptions?.(assistantSpeaking ? BARGE_IN_VAD_OPTIONS : NORMAL_VAD_OPTIONS);

        if (!enabledRef.current || !vadRef.current) return;

        if (disabled) {
            clearBargeInTimer();
            clearIdleTimer();
            pauseVad();
            setMode('thinking');
        } else if (assistantSpeaking) {
            clearIdleTimer();
            startVad();
            setMode('speaking');
        } else {
            clearBargeInTimer();
            resumeTimerRef.current = setTimeout(() => {
                if (!enabledRef.current || stoppedRef.current || busyRef.current || assistantSpeakingRef.current) return;
                startVad();
                armIdleTimer();
            }, SPEAKER_TAIL_SUPPRESS_MS);
        }
    }, [armIdleTimer, assistantSpeaking, clearBargeInTimer, clearIdleTimer, clearResumeTimer, disabled, pauseVad, startVad]);

    useEffect(() => destroySilently, [destroySilently]);

    return {
        enabled,
        mode,
        label: handsFreeLabels[mode] || handsFreeLabels.off,
        error,
        start,
        stop,
        toggle,
    };
}
